import {useState} from 'react';
import gql from 'graphql-tag';
import {useQuery} from 'services/apollo';
import {EzPage, EzHeading, EzBanner} from '@ezcater/recipe';
import {useTranslation} from 'react-i18next';
import {startOfDay, endOfDay} from 'date-fns';
import {
  type MyDeliveriesAssignmentsQuery,
  type MyDeliveriesAssignmentsQueryVariables,
  DriverAssignmentState,
  SortDirection,
} from 'services/graphql/types';
import DeliveryCardContainer from 'components/DeliveryCardContainer';
import {FlashMessages} from '@ezcater/flash-messages';
import localStorageData from 'util/localStorageData';
import TrainingVideo from 'constants/TrainingVideo';
import Layout from '../Layout';

export const AssignmentsQuery = gql(/* GraphQL */ `
  query MyDeliveriesAssignmentsQuery($state: DriverAssignmentState, $sortDirection: SortDirection) {
    me {
      driverAccount {
        assignments(state: $state, sortDirection: $sortDirection) {
          nodes {
            dropoffAddress {
              city
              id
              state
              street1
              street2
              zip
            }
            eventAt
            formattedOrderNumber
            id
            pickupAddress {
              id
              timeZoneIdentifier
            }
            status
          }
        }
        id
      }
    }
  }
`);

const MyDeliveries = () => {
  const {t} = useTranslation();
  const showGettingStartedStorage = localStorageData('showGettingStarted', true);
  const [showGettingStarted, setShowGettingStarted] = useState(showGettingStartedStorage.get());
  const hideGettingStarted = () => {
    setShowGettingStarted(showGettingStartedStorage.set(false));
  };

  const today = new Date();

  const {loading, data} = useQuery<
    MyDeliveriesAssignmentsQuery,
    MyDeliveriesAssignmentsQueryVariables
  >(AssignmentsQuery, {
    variables: {state: DriverAssignmentState.Scheduled, sortDirection: SortDirection.Asc},
  });

  const todaysAssignments = data?.me?.driverAccount?.assignments.nodes.filter(
    assignment =>
      new Date(assignment.eventAt) <= endOfDay(today) &&
      new Date(assignment.eventAt) >= startOfDay(today)
  );
  const upcomingAssignments = data?.me?.driverAccount?.assignments.nodes.filter(
    assignment => new Date(assignment.eventAt) > endOfDay(today)
  );

  const title = t('myDeliveries.title');
  const header = t('myDeliveries.header');
  const todayTableTitle = t('myDeliveries.tableTitleToday');
  const upcomingTableTitle = t('myDeliveries.tableTitleUpcoming');
  const gettingStartedTitle = t('myDeliveries.gettingStarted.title');
  const gettingStartedMessage = t('myDeliveries.gettingStarted.message');
  const gettingStartedLink = t('myDeliveries.gettingStarted.link');

  const gettingStartedBanner = showGettingStarted ? (
    <div data-testid="gs-banner">
      <EzBanner
        title={gettingStartedTitle}
        message={gettingStartedMessage}
        link={{
          label: gettingStartedLink,
          onClick: () => {
            window.open(TrainingVideo.url, TrainingVideo.target);
          },
        }}
        use="ezOrdering"
        onDismiss={hideGettingStarted}
      />
    </div>
  ) : (
    false
  );

  return (
    <Layout title={title}>
      <EzPage>
        {gettingStartedBanner}
        <FlashMessages />
        <EzHeading size="1">{header}</EzHeading>
        <DeliveryCardContainer
          assignments={todaysAssignments}
          loading={loading}
          header={todayTableTitle}
          dataTestId="todays-deliveries"
        />
        <DeliveryCardContainer
          assignments={upcomingAssignments}
          loading={loading}
          header={upcomingTableTitle}
          dataTestId="upcoming-deliveries"
        />
      </EzPage>
    </Layout>
  );
};

export default MyDeliveries;
